import { useEffect, useRef } from 'react'
import { useAuth } from 'react-oidc-context'

import { createSigninRedirectArgs } from '@/lib/authRedirect'
import { setUnauthorizedHandler } from '@/lib/queryClient'
import { useAuthStore } from '@/lib/stores/auth'
import { useStationStore } from '@/lib/stores/station'

type UseAuthCoordinatorOptions = {
  onBeforeUnauthorizedRedirect?: () => void
}

export const useAuthCoordinator = ({
  onBeforeUnauthorizedRedirect,
}: UseAuthCoordinatorOptions = {}) => {
  const auth = useAuth()
  const { removeUser, signinRedirect, isAuthenticated, user } = auth
  const isHandlingUnauthorizedRef = useRef(false)

  useEffect(() => {
    setUnauthorizedHandler(async () => {
      if (isHandlingUnauthorizedRef.current) {
        return
      }

      isHandlingUnauthorizedRef.current = true
      onBeforeUnauthorizedRedirect?.()
      useAuthStore.getState().clearAuth()
      useStationStore.getState().clearSelectedStationCode()

      try {
        await Promise.resolve(removeUser?.())
      } catch {
        // no-op
      }

      try {
        await signinRedirect(createSigninRedirectArgs())
      } catch {
        isHandlingUnauthorizedRef.current = false
      }
    })

    return () => {
      setUnauthorizedHandler(null)
    }
  }, [onBeforeUnauthorizedRedirect, removeUser, signinRedirect])

  useEffect(() => {
    if (isAuthenticated && user && !user.expired) {
      isHandlingUnauthorizedRef.current = false
    }
  }, [isAuthenticated, user])
}
